import { useMemo, useState } from 'react';
import { IconAlertTriangle, IconArrowForwardUp, IconCalendar, IconChartBar, IconClockHour3, IconDownload, IconExternalLink, IconFilter, IconUser } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Drawer, Skeleton, StateView, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import './roePhone.i18n';
import './ServiceGP.i18n';
import { financeScreenState, useFinanceAccess } from './access';
import { DEFAULT_GP_FILTER, GP_JOBS, GP_PICS, GP_SERVICES, aggregate, filterJobs, groupRows, pct, pct1, tr, type GpBy, type GpFilter, type GpPer, type GpSv } from './servicegpData';
import s from './servicegp.module.css';
import q from './servicegpPad.module.css';

// Mint-ServiceGP (1536 · 1280 · 1920): GP theo dịch vụ / người phụ trách — kỳ · lọc · 6 số tổng · bảng 9 cột + dòng Tổng.
// GP = REV − (BIL + ABS); OBH, COL không tính GP. TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast.

const PERIODS: readonly (readonly [GpPer, string])[] = [['m10', 'sgp.m10'], ['m09', 'sgp.m09'], ['rng', 'sgp.rng']];
const BYS: readonly (readonly [GpBy, string])[] = [['svc', 'sgp.svc'], ['pic', 'sgp.pic']];
const dk = (x: string) => `${x.slice(8, 10)}/${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+x.slice(5, 7) - 1]}`;
const SV_NAME = Object.fromEntries(GP_SERVICES) as Record<GpSv, string>;
const SV_PICS: Record<string, string> = { CCF: 'HOATTN · NHIDTT', COO: 'NHIDTT · HOATTN', FUM: 'HOATTN', SCY: 'PHONGNT · ANNV', PTF: 'NHIDTT', DDL: 'HOATTN', TRF: 'THANHNT' };

export function ServiceGP() {
  const toast = useToast();
  const tx = useT();
  const pad = useLayout() === 'pad';
  const access = useFinanceAccess();
  const [f, setF] = useState<GpFilter>(DEFAULT_GP_FILTER);
  const [by, setBy] = useState<GpBy>('svc');
  const state = financeScreenState(access);
  const noWrite = () => toast({ msg: tx('sgp.nowrite') });

  const list = useMemo(() => (state === 'empty' ? [] : filterJobs(GP_JOBS, f)), [state, f]);
  const all = useMemo(() => aggregate(list), [list]);
  const rows = useMemo(() => groupRows(list, by), [list, by]);
  const perK = f.per === 'm10' ? tx('sgp.m10y') : f.per === 'm09' ? tx('sgp.m09y') : `${dk(f.from)} → ${dk(f.to)}`;
  const byLab = by === 'svc' ? tx('sgp.svc') : tx('sgp.pic');
  const kpis = [
    ['REV', tr(all.rev), '', tx('sgp.tipRev')],
    ['BIL + ABS', tr(all.cost), '', `BIL ${tr(all.cost - all.abs)} · ABS ${tr(all.abs)}`],
    ['GP = REV − (BIL + ABS)', tr(all.gp), all.gp < 0 ? s.err : s.okc, tx('sgp.tipGp')],
    ['GP%', pct1(all.gpP), all.gp < 0 ? s.err : s.okc, tx('sgp.tipGpp')],
    [tx('sgp.kDev'), pct(all.devP), all.flag ? s.warn : '', `${tx('sgp.tipDev')} · ${all.dev > 0 ? '+' : ''}${tr(all.dev)}`],
    [tx('sgp.kTreo'), tr(all.treo), all.treo ? s.warn : s.okc, `OBH ${tr(all.obh)} ${tx('sgp.tipObh')} · COL ${tr(all.col)} ${tx('sgp.tipCol')}`],
  ] as const;
  const ready = state === 'ready' || state === 'empty';
  const [dr, setDr] = useState<string | null>(null);
  const cur = dr ? rows.find((r) => r.key === dr) ?? null : null;
  const curJobs = useMemo(
    () => (cur ? list.filter((j) => (by === 'svc' ? j.sv : j.pic) === cur.key).sort((a, b) => (a.done < b.done ? 1 : -1)) : []),
    [cur, list, by],
  );
  const drawerSoon = (key: string) => setDr(key);
  const upd = (p: Partial<GpFilter>) => { setDr(null); setF({ ...f, ...p }); };

  return (
    <Page kind="table">
      <PageHeader
        title={tx('sgp.title')}
        findHint={false}
        meta={ready && all.wait > 0 ? <span className={s.wait} title={tx('sgp.tipWaitHead')}><IconClockHour3 size={13} aria-hidden="true" />{all.wait} {tx('sgp.waitL')}</span> : undefined}
        actions={
          <>
            {pad && ready && (
              <div className={`${s.seg} ${q.seg}`} role="tablist" aria-label={tx('sgp.groupBy')}>
                {BYS.map(([k, l]) => (
                  <button key={k} type="button" role="tab" aria-selected={by === k} onClick={() => { setDr(null); setBy(k); }}>{tx(l)}</button>
                ))}
              </div>
            )}
            <HeaderTool label="Service Hub" icon={IconExternalLink} onClick={() => toast({ msg: tx('sgp.hub') })} />
            <Button variant="primary" icon={<IconDownload size={16} aria-hidden="true" />} onClick={noWrite}>{tx('fng.export')}</Button>
          </>
        }
      />
      <div className={`${s.page} ${pad ? q.page : ''}`}>
        <div className={s.bar}>
          <div className={s.seg} role="tablist" aria-label={tx('sgp.period')}>
            {PERIODS.map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={f.per === k} onClick={() => upd({ per: k })}>{tx(l)}</button>
            ))}
          </div>
          {f.per === 'rng' && (
            <>
              <label className={s.fld} title={tx('sgp.fromT')}>
                <IconCalendar size={15} aria-hidden="true" />
                <input type="date" aria-label={tx('sgp.from')} value={f.from} onChange={(e) => upd({ from: e.target.value || f.from })} />
              </label>
              <label className={s.fld} title={tx('sgp.toT')}>
                →<input type="date" aria-label={tx('sgp.to')} value={f.to} onChange={(e) => upd({ to: e.target.value || f.to })} />
              </label>
            </>
          )}
          <label className={s.fld}>
            <IconFilter size={15} aria-hidden="true" />
            <select aria-label={tx('sgp.svc')} className={s.s150} value={f.sv} onChange={(e) => upd({ sv: e.target.value as GpSv | 'all' })}>
              <option value="all">{tx('fng.allSvc')}</option>
              {GP_SERVICES.map(([k, n]) => <option key={k} value={k}>{k} · {n}</option>)}
            </select>
          </label>
          <label className={s.fld}>
            <IconFilter size={15} aria-hidden="true" />
            <select aria-label={tx('sgp.pic')} className={s.s170} value={f.pic} onChange={(e) => upd({ pic: e.target.value })}>
              <option value="all">{tx('fng.allOwner')}</option>
              {GP_PICS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          {ready && <span className={s.per} title={tx('sgp.perT')}><IconCalendar size={15} aria-hidden="true" />{perK} · {all.n} {tx('fng.job')}</span>}
        </div>
        <StateView state={ready ? 'ready' : state} title={state === 'noperm' ? tx('sgp.noperm') : undefined} skeleton={<Skeleton rows={4} />}>
          <div className={`${s.kpis} ${pad ? q.kpis : ''}`}>
            {kpis.map(([k, v, tone, tip]) => (
              <div key={k} className={s.kpi} title={tip}>
                <div>{k}</div>
                <b className={tone}>{v}</b>
              </div>
            ))}
          </div>
          {pad ? (
            <>
              <div className={q.cards}>
                {rows.map((r) => (
                  <button key={r.key} type="button" className={q.card} aria-label={`${tx('sgp.view')} · ${r.key}`} onClick={() => drawerSoon(r.key)}>
                    <span className={q.top}>
                      {by === 'svc' ? <span className={`${q.tile} ${s[`sv_${r.key}`]}`}>{r.key}</span> : <span className={q.av}>{r.key.slice(0, 2)}</span>}
                      <span className={q.ttl}>
                        <b>{by === 'svc' ? SV_NAME[r.key as GpSv] : r.key}</b>
                        <span>{r.n} {tx('fng.job')} · REV {tr(r.rev)} · BIL + ABS {tr(r.cost)}</span>
                      </span>
                      <span className={q.gp}>
                        <b className={r.gp < 0 ? s.err : ''}>{tr(r.gp)}</b>
                        <span>{pct1(r.gpP)}</span>
                      </span>
                    </span>
                    <span className={q.track}><span className={r.gp < 0 ? s.fe : s.fd} style={{ width: `${r.w}%` }} /></span>
                    <span className={q.chips}>
                      <span className={r.flag ? s.warn : s.lbc} title={r.flag ? `${tx('sgp.devC')} ${pct(r.devP)} · ${tx('sgp.devHead')}` : tx('sgp.devT')}>
                        <IconAlertTriangle size={14} aria-hidden="true" />{tx('sgp.dev')} {pct(r.devP)}
                      </span>
                      <span className={r.wait ? s.warn : s.lbc} title={tx('sgp.tipWait')}><IconClockHour3 size={14} aria-hidden="true" />{r.wait} {tx('sgp.waitL')}</span>
                      <span className={r.treo ? s.warn : s.okc} title={`OBH ${tr(r.obh)} · COL ${tr(r.col)}${r.treo ? ` · ${tx('sgp.unbal')}` : ` · ${tx('sgp.bal')}`}`}>
                        <IconArrowForwardUp size={14} aria-hidden="true" />OBH · COL {r.treo ? tr(r.treo) : tx('fng.even')}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              {rows.length === 0 && <div className={s.none}>{tx('fng.none')}</div>}
            </>
          ) : (
          <section className={s.card} aria-label={`${tx('sgp.gpBy')} ${byLab}`}>
            <div className={s.ch}>
              <IconChartBar size={18} aria-hidden="true" />
              <h2>{tx('sgp.gpBy')} {byLab}</h2>
              <div className={`${s.seg} ${s.segR}`} role="tablist" aria-label={tx('sgp.groupBy')}>
                {BYS.map(([k, l]) => (
                  <button key={k} type="button" role="tab" aria-selected={by === k} onClick={() => { setDr(null); setBy(k); }}>{tx(l)}</button>
                ))}
              </div>
            </div>
            <div className={`${s.g} ${s.gh}`} aria-hidden="true">
              <span>{byLab}</span><span className={s.r}>{tx('fng.job')}</span><span className={s.r}>REV</span><span className={s.r}>BIL + ABS</span><span className={s.r}>GP</span><span>GP%</span><span className={s.r}>{tx('fng.dev')}</span><span className={s.r}>{tx('fng.wait')}</span><span className={s.r}>OBH · COL</span>
            </div>
            <div className={s.body}>
              {rows.map((r) => (
                <button key={r.key} type="button" className={`${s.g} ${s.row}`} aria-label={`${tx('sgp.view')} · ${r.key}`} onClick={() => drawerSoon(r.key)}>
                  <span className={s.nm}>
                    {by === 'svc' ? <span className={`${s.tile} ${s[`sv_${r.key}`]}`}>{r.key}</span> : <span className={s.av}>{r.key.slice(0, 2)}</span>}
                    <span className={s.nt}>
                      <b>{by === 'svc' ? SV_NAME[r.key as GpSv] : r.key}</b>
                      <span title={tx('sgp.pic')}>{by === 'svc' ? SV_PICS[r.key] : tx('sgp.pic')}</span>
                    </span>
                  </span>
                  <span className={s.r}>{r.n}</span>
                  <span className={s.r}>{tr(r.rev)}</span>
                  <span className={s.r} title={`BIL ${tr(r.cost - r.abs)} · ABS ${tr(r.abs)}`}>{tr(r.cost)}</span>
                  <b className={`${s.r} ${r.gp < 0 ? s.err : ''}`}>{tr(r.gp)}</b>
                  <span className={s.gpc}>
                    <span className={s.track}><span className={r.gp < 0 ? s.fe : s.fd} style={{ width: `${r.w}%` }} /></span>
                    <span className={s.pc}>{pct1(r.gpP)}</span>
                  </span>
                  <span className={`${s.dev} ${r.flag ? s.warn : s.lbc}`} title={r.flag ? `${tx('sgp.devC')} ${pct(r.devP)} · ${tx('sgp.devHead')}` : tx('sgp.devT')}>
                    {r.flag && <IconAlertTriangle size={13} aria-hidden="true" />}{pct(r.devP)}
                  </span>
                  <span className={`${s.dev} ${r.wait ? s.warn : s.lbc}`} title={tx('sgp.tipWait')}><IconClockHour3 size={13} aria-hidden="true" />{r.wait}</span>
                  <span className={`${s.r} ${r.treo ? s.warn : s.okc}`} title={`OBH ${tr(r.obh)} · COL ${tr(r.col)}${r.treo ? ` · ${tx('sgp.unbal')}` : ` · ${tx('sgp.bal')}`}`}>{r.treo ? tr(r.treo) : tx('fng.even')}</span>
                </button>
              ))}
              {rows.length === 0 && <div className={s.none}>{tx('fng.none')}</div>}
            </div>
            {rows.length > 0 && (
              <div className={`${s.g} ${s.tot}`}>
                <span>{tx('fng.total')}</span><span className={s.r}>{all.n}</span><span className={s.r}>{tr(all.rev)}</span><span className={s.r}>{tr(all.cost)}</span><span className={s.r}>{tr(all.gp)}</span><span>{pct1(all.gpP)}</span><span className={s.r}>{pct(all.devP)}</span><span className={s.r}>{all.wait}</span><span className={s.r}>{all.treo ? tr(all.treo) : tx('fng.even')}</span>
              </div>
            )}
          </section>
          )}
        </StateView>
      </div>
      <Drawer
        open={!!cur}
        onOpenChange={(o) => !o && setDr(null)}
        title={cur ? (by === 'svc' ? `${cur.key} · ${SV_NAME[cur.key as GpSv]}` : cur.key) : ''}
        description={cur ? `${cur.n} ${tx('fng.job')} · ${perK}` : undefined}
        closeLabel={tx('sgp.close')}
      >
        {cur && (
          <div className={s.dw}>
            <div className={s.dwKpi}>
              {([['REV', tr(cur.rev)], ['BIL + ABS', tr(cur.cost)], ['GP', tr(cur.gp)], ['GP%', pct1(cur.gpP)]] as const).map(([k, v]) => (
                <div key={k}><div>{k}</div><b>{v}</b></div>
              ))}
            </div>
            <ul className={s.dwList}>
              {curJobs.map((j) => {
                const gp = j.rev - j.cost, dev = j.cost - j.plan, devP = j.plan ? (dev / j.plan) * 100 : 0;
                const fl = Math.abs(dev) > 500000 || Math.abs(devP) > 5;
                const treo = j.obh + j.col;
                return (
                  <li key={j.id} className={s.dwRow}>
                    <div className={s.dwTop}>
                      <a href={`/ezy/jobs/${j.id}`}>{j.id}</a>
                      <span>{j.sv}</span>
                      <span className={s.dwCus}>{j.cus}</span>
                      <b className={gp < 0 ? s.err : ''}>{tr(gp)}</b>
                    </div>
                    <div className={s.dwMeta}>
                      <span title={`${tx('sgp.pic')} ${j.pic}`}><IconUser size={13} aria-hidden="true" />{j.pic}</span>
                      <span title={tx('sgp.recT')}><IconCalendar size={13} aria-hidden="true" />{dk(j.done)}</span>
                      <span title={`REV · BIL ${tr(j.cost - j.abs)} · ABS ${tr(j.abs)}`}>REV {tr(j.rev)} · BIL + ABS {tr(j.cost)}</span>
                      <span className={fl ? s.warn : s.lbc} title={tx('sgp.devT')}>{fl && <IconAlertTriangle size={13} aria-hidden="true" />}{pct(devP)}</span>
                      {j.wait > 0 && <span className={s.warn} title={tx('sgp.tipWait')}><IconClockHour3 size={13} aria-hidden="true" />{j.wait}</span>}
                      {treo > 0 && <span className={s.warn} title={`OBH ${tr(j.obh)} · COL ${tr(j.col)} · ${tx('sgp.unbal')}`}><IconArrowForwardUp size={13} aria-hidden="true" />{tr(treo)}</span>}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Drawer>
    </Page>
  );
}
